import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Inbox, Megaphone, Loader2, CheckCircle2, AlertTriangle, ArrowRight, Activity } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/kit";
import CountUp from "@/components/CountUp";

const DOC_READY = ["ready", "partially_processed"];
const DOC_PROC = (s) => !["ready", "partially_processed", "failed", "deleted"].includes(s);

const container = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 320, damping: 28 } } };

export default function Overview() {
  const { user } = useAuth();
  const [knowledge, setKnowledge] = useState([]);
  const [docs, setDocs] = useState([]);
  const [pending, setPending] = useState(0);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/knowledge?status=current").then(({ data }) => setKnowledge(data)).catch(() => {});
    api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
    api.get("/pending/count").then(({ data }) => setPending(data.count || 0)).catch(() => {});
    api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  }, []);

  const ready = docs.filter((d) => DOC_READY.includes(d.status)).length;
  const processing = docs.filter((d) => DOC_PROC(d.status)).length;
  const failed = docs.filter((d) => d.status === "failed").length;
  const firstName = user?.name?.split(" ")[0] || "";

  const tiles = [
    { icon: Brain, label: "Conhecimentos no cérebro", value: knowledge.length, to: "/app/brain", chip: "bg-indigo-50 text-indigo-600" },
    { icon: CheckCircle2, label: "Documentos prontos", value: ready, to: "/app/brain", chip: "bg-emerald-50 text-emerald-600" },
    { icon: Loader2, label: "Documentos processando", value: processing, to: "/app/brain", chip: "bg-amber-50 text-amber-600", spin: processing > 0 },
    { icon: Inbox, label: "Pendências aguardando", value: pending, to: "/app/pending", chip: "bg-sky-50 text-sky-600" },
  ];

  const actions = [
    { icon: Brain, label: "Alimentar o cérebro", desc: "Escrever conhecimento ou enviar documentos", to: "/app/brain" },
    { icon: Inbox, label: "Responder pendências", desc: `${pending} pergunta(s) aguardando resposta`, to: "/app/pending" },
    { icon: Megaphone, label: "Nova campanha", desc: "Comunicar funcionários no WhatsApp", to: "/app/campaigns" },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{firstName ? `Olá, ${firstName}.` : "Painel de operação"}</h1>
        <p className="mt-1 text-sm text-slate-500">Estado da operação da Isa. Alimente o cérebro, responda o que ela não soube e mantenha tudo funcionando.</p>
      </div>

      {failed > 0 && (
        <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="failed-docs-alert">
          <AlertTriangle size={16} /> {failed} documento(s) falharam no processamento. <Link to="/app/brain" className="font-medium underline">Verificar</Link>
        </div>
      )}

      <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((t, i) => (
          <motion.div key={i} variants={item}>
            <Link to={t.to} data-testid={`op-stat-${i}`}
              className="group flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_15px_rgb(0,0,0,0.02)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-12px_rgba(11,18,32,0.12)]">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${t.chip} transition-transform duration-300 group-hover:scale-110`}>
                <t.icon size={20} strokeWidth={1.9} className={t.spin ? "animate-spin" : ""} />
              </span>
              <CountUp value={t.value} className="font-display text-4xl font-bold tracking-tight text-slate-900" />
              <p className="text-sm text-slate-500">{t.label}</p>
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="mb-4 font-display font-semibold text-slate-900">Ações rápidas</h2>
          <div className="space-y-2" data-testid="quick-actions">
            {actions.map((a, i) => (
              <Link key={i} to={a.to} className="group flex items-center gap-3 rounded-2xl border border-slate-100 px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-200 hover:bg-slate-50 hover:shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform duration-200 group-hover:scale-110"><a.icon size={18} /></span>
                <div className="min-w-0 flex-1"><p className="font-medium text-slate-900">{a.label}</p><p className="text-xs text-slate-500">{a.desc}</p></div>
                <ArrowRight size={16} className="text-slate-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-slate-500" />
              </Link>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2"><Activity size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-slate-900">Campanhas recentes</h2></div>
          <div className="space-y-2" data-testid="recent-campanhas">
            {campaigns.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-2xl border border-slate-100 px-3 py-2.5 text-sm transition-colors hover:bg-slate-50">
                <div className="min-w-0"><p className="truncate font-medium text-slate-900">{c.title}</p><p className="text-xs text-slate-400">{String(c.sent_at).slice(0, 10)}</p></div>
                <span className="shrink-0 text-xs text-slate-500">{c.recipient_count} enviadas</span>
              </div>
            ))}
            {!campaigns.length && <p className="text-sm text-slate-500">Nenhuma campanha enviada ainda.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
